/**
 * De routes van werkplek v6, op één plek. `App.tsx` neemt hier één regel
 * van over.
 *
 * `/` is Vandaag (v6), `/beheer` de lijst met beheerschermen (v6, sessie
 * 70).
 * De overige pagina's draaien ongewijzigd in
 * `OudScherm` onder dezelfde menubalk, tot ze in W5-2 en W5-3 herbouwd
 * worden.
 */
import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route } from "react-router-dom";

import { AdminRoute } from "@/components/auth/AdminRoute";

import { WerkplekAfwezigheid } from "../Werkplek/Afwezigheid/WerkplekAfwezigheid";
import { WerkplekToegang } from "../Werkplek/Beheer/WerkplekToegang";
import { WerkplekGezondheid } from "../Werkplek/Gezondheid/WerkplekGezondheid";
import { WerkplekKennis } from "../Werkplek/Kennis/WerkplekKennis";
import { WerkplekV6Klanten } from "./Klanten/KlantenPagina";
import { WerkplekRetouren } from "../Werkplek/Retouren/WerkplekRetouren";
import { WerkplekReviews } from "../Werkplek/Reviews/WerkplekReviews";
import { WerkplekBeheer } from "../Werkplek/WerkplekBeheer";
import { WerkplekMijnProfiel } from "../Werkplek/WerkplekMijnProfiel";
import { WerkplekSignalen } from "../Werkplek/WerkplekSignalen";
import { WerkplekVoorraad } from "../Werkplek/WerkplekVoorraad";
import { KnowledgeFormPagina } from "../Admin/KnowledgeForm";
import { RetourAanmakenPagina } from "../Admin/RetourAanmaken";
import { ProfielPagina } from "../Profiel";

import { WerkplekV6BeheerLijst } from "./Beheer/BeheerLijst";
import { WerkplekV6CheckoutBeheer } from "./Beheer/CheckoutBeheer";
import { OudScherm } from "./OudScherm";
import { WerkplekV6Shell } from "./Shell";
import { WerkplekV6Vandaag } from "./Vandaag";

// Sessie 70: de achttien oude beheerschermen laden pas als ze geopend
// worden, zodat ze niet in de hoofdbundel (ook op de iPhone) zitten.
// Checkout 16a: het nieuwe meetscherm, in de stijl van Beheer > Checkout.
const CheckoutMetingPagina = lazy(() => import("./Beheer/CheckoutMeting"));
const OptinsPagina = lazy(() => import("../Admin/Optins"));
const RecoveryMailsPagina = lazy(() => import("../Admin/RecoveryMails"));
const StoringenPagina = lazy(() => import("../Admin/Storingen"));
const VoorraadmeldingenPagina = lazy(() => import("../Admin/Voorraadmeldingen"));
const CorrectieDashboardPagina = lazy(() =>
  import("../Admin/CorrectieDashboard").then((m) => ({ default: m.CorrectieDashboardPagina })),
);
const HugoSleutelsPagina = lazy(() =>
  import("../Admin/HugoSleutels").then((m) => ({ default: m.HugoSleutelsPagina })),
);
const KwaliteitsrunPagina = lazy(() =>
  import("../Admin/Kwaliteitsrun").then((m) => ({ default: m.KwaliteitsrunPagina })),
);
const MailboxenBeheerPagina = lazy(() =>
  import("../Admin/MailboxenBeheer").then((m) => ({ default: m.MailboxenBeheerPagina })),
);
const MedewerkerFormPagina = lazy(() =>
  import("../Admin/MedewerkerForm").then((m) => ({ default: m.MedewerkerFormPagina })),
);
const MedewerkersPagina = lazy(() =>
  import("../Admin/Medewerkers").then((m) => ({ default: m.MedewerkersPagina })),
);
const ProductGatenPagina = lazy(() =>
  import("../Admin/ProductGaten").then((m) => ({ default: m.ProductGatenPagina })),
);
const SanneToetsPagina = lazy(() =>
  import("../Admin/SanneToets").then((m) => ({ default: m.SanneToetsPagina })),
);
const ServicecijfersPagina = lazy(() =>
  import("../Admin/Servicecijfers").then((m) => ({ default: m.ServicecijfersPagina })),
);
const SjablonenPagina = lazy(() =>
  import("../Admin/Sjablonen").then((m) => ({ default: m.SjablonenPagina })),
);
const WebshopExtrasPagina = lazy(() =>
  import("../Admin/WebshopExtras").then((m) => ({ default: m.WebshopExtrasPagina })),
);
const WhatsAppTemplatesPagina = lazy(() =>
  import("../Admin/WhatsAppTemplates").then((m) => ({ default: m.WhatsAppTemplatesPagina })),
);
const ZoekenBeheerPagina = lazy(() =>
  import("../Admin/Zoeken").then((m) => ({ default: m.ZoekenBeheerPagina })),
);

/**
 * Sessie 8D: adressen van weggehaalde beheerschermen (Stoplicht, Motor 6.1,
 * Dit leest V8, Motor V8 live). Een bladwijzer landt op de Beheer-lijst in
 * plaats van op een lege pagina.
 */
const WEGGEHAALDE_BEHEERPADEN = [
  "beheer/stoplicht",
  "beheer/motor",
  "beheer/v8-leest",
  "beheer/v8-live",
] as const;

/** Een scherm uit het oude portaal, scrollend binnen het werkvlak. */
function OudBeheer({ children }: { children: ReactNode }) {
  return (
    <div className="wp-inhoud" style={{ overflow: "auto", padding: 24 }}>
      <Suspense fallback={<p className="v6-leeg">Laden</p>}>{children}</Suspense>
    </div>
  );
}

export const werkplekV6Routes = (
  <Route path="/" element={<WerkplekV6Shell />}>
    <Route index element={<WerkplekV6Vandaag />} />
    {/* Sessie 70 (W5-3): Beheer is een lijst met alle beheerschermen.
        De instellingen staan op beheer/instellingen; een oud adres met
        zoekstring stuurt de lijst daar zelf heen. */}
    <Route path="beheer" element={<WerkplekV6BeheerLijst />} />
    {WEGGEHAALDE_BEHEERPADEN.map((pad) => (
      <Route key={pad} path={`${pad}/*`} element={<Navigate to="/beheer" replace />} />
    ))}
    <Route element={<AdminRoute />}>
      {/* Checkout 11: één beheerscherm voor de checkoutlaag (schakelaars,
          ordercontrole, trechter, bewaking, ankers). Vervangt de
          schakelaars van sessie 70 (B3) op hetzelfde adres. */}
      <Route path="beheer/checkout" element={<WerkplekV6CheckoutBeheer />} />
    </Route>
    {/* Sessie 73: klantkaart v6 (ontwerp 31). */}
    <Route path="klanten" element={<WerkplekV6Klanten />} />
    <Route path="klanten/:klantId" element={<WerkplekV6Klanten />} />
    <Route element={<OudScherm />}>
      <Route path="retouren" element={<WerkplekRetouren />} />
      {/* Sessie 67: schermen uit het oude portaal waar de werkplek zelf
          naar linkt, ongewijzigd, tot W5-2/W5-3 (zie BESLISSINGEN). */}
      <Route path="retouren/nieuw" element={<OudBeheer><RetourAanmakenPagina /></OudBeheer>} />
      <Route path="kennis/bewerken/:kennisId" element={<OudBeheer><KnowledgeFormPagina /></OudBeheer>} />
      <Route path="reviews" element={<WerkplekReviews />} />
      <Route path="signalen" element={<WerkplekSignalen />} />
      <Route path="voorraad" element={<WerkplekVoorraad />} />
      <Route path="kennis" element={<WerkplekKennis />} />
      <Route path="gezondheid" element={<WerkplekGezondheid />} />
      {/* Sessie 70: de API van Instellingen is alleen voor beheerders. */}
      <Route element={<AdminRoute />}>
        <Route path="beheer/instellingen" element={<WerkplekBeheer />} />
      </Route>
      <Route path="beheer/gebruikers" element={<WerkplekToegang />} />
      <Route path="beheer/afwezigheid" element={<WerkplekAfwezigheid />} />
      {/* Sessie 70 (W5-3): de achttien beheerschermen uit het oude
          portaal, ongewijzigd teruggezet uit #698 en alleen voor
          beheerders, zoals de API erachter. Herbouwen komt later. */}
      <Route element={<AdminRoute />}>
        <Route path="beheer/medewerkers" element={<OudBeheer><MedewerkersPagina /></OudBeheer>} />
        <Route path="beheer/medewerkers/nieuw" element={<OudBeheer><MedewerkerFormPagina /></OudBeheer>} />
        <Route path="beheer/medewerkers/:medewerkerId" element={<OudBeheer><MedewerkerFormPagina /></OudBeheer>} />
        <Route path="beheer/mailboxen" element={<OudBeheer><MailboxenBeheerPagina /></OudBeheer>} />
        <Route path="beheer/sjablonen" element={<OudBeheer><SjablonenPagina /></OudBeheer>} />
        <Route path="beheer/whatsapp-templates" element={<OudBeheer><WhatsAppTemplatesPagina /></OudBeheer>} />
        <Route path="beheer/hugo-sleutels" element={<OudBeheer><HugoSleutelsPagina /></OudBeheer>} />
        <Route path="beheer/storingen" element={<OudBeheer><StoringenPagina /></OudBeheer>} />
        <Route path="beheer/servicecijfers" element={<OudBeheer><ServicecijfersPagina /></OudBeheer>} />
        <Route
          path="beheer/checkout-meting"
          element={<Suspense fallback={<p className="v6-leeg">Laden</p>}><CheckoutMetingPagina /></Suspense>}
        />
        <Route path="beheer/optins" element={<OudBeheer><OptinsPagina /></OudBeheer>} />
        <Route path="beheer/recovery-mails" element={<OudBeheer><RecoveryMailsPagina /></OudBeheer>} />
        <Route path="beheer/voorraadmeldingen" element={<OudBeheer><VoorraadmeldingenPagina /></OudBeheer>} />
        <Route path="beheer/product-gaten" element={<OudBeheer><ProductGatenPagina /></OudBeheer>} />
        <Route path="beheer/webshop-extras" element={<OudBeheer><WebshopExtrasPagina /></OudBeheer>} />
        <Route path="beheer/zoeken" element={<OudBeheer><ZoekenBeheerPagina /></OudBeheer>} />
        <Route path="beheer/kwaliteitsrun" element={<OudBeheer><KwaliteitsrunPagina /></OudBeheer>} />
        <Route path="beheer/sanne-toets" element={<OudBeheer><SanneToetsPagina /></OudBeheer>} />
        <Route path="beheer/correctie-dashboard" element={<OudBeheer><CorrectieDashboardPagina /></OudBeheer>} />
      </Route>
      <Route path="profiel" element={<WerkplekMijnProfiel />} />
      {/* Wachtwoord, 2FA en e-mailhandtekening: het oude profielscherm,
          ongewijzigd, tot het in W5-3 in Beheer komt (sessie 67). Dat
          scherm raakt beveiliging; het verdween niet mee met /oud. */}
      <Route
        path="profiel/beveiliging"
        element={
          <OudBeheer>
            <ProfielPagina />
          </OudBeheer>
        }
      />
    </Route>
  </Route>
);
